import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import { Calendar, UserCheck, CreditCard, ExternalLink, ShieldCheck } from 'lucide-react';
import { showSuccess, showError } from '../../utils/swal';

const SubscriptionInfo = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    subscription: null,
    plan: null,
    usage: null
  });

  useEffect(() => {
    fetchInfo();
  }, []);

  const fetchInfo = async () => {
    try {
      const [subRes, usageRes] = await Promise.all([
        api.get('/office/subscription'),
        api.get('/office/usage')
      ]);

      setData({
        subscription: subRes.data?.data?.subscription,
        plan: subRes.data?.data?.plan,
        usage: usageRes.data?.data
      });
    } catch (error) {
      console.error('Bilgiler alınamadı:', error);
      showError('Abonelik bilgileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Yükleniyor...</div>;

  const { subscription, plan, usage } = data;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">Aktif</span>;
      case 'trial': return <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">Deneme Sürümü</span>;
      case 'past_due': return <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">Ödeme Gecikti</span>;
      case 'cancelled': return <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">İptal Edildi</span>;
      default: return null;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const usersCount = usage?.current_users || 0;
  const maxUsers = usage?.max_users || 0;
  const usagePercentage = maxUsers > 0 ? (usersCount / maxUsers) * 100 : 0;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Mevcut Plan Özeti */}
        <Card title="Abonelik Bilgileriniz">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Mevcut Plan</p>
                <p className="text-2xl font-bold text-gray-900">{plan?.name || 'Limitsiz Özel Plan'}</p>
              </div>
              <div>
                {getStatusBadge(subscription?.status)}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg flex items-center gap-3">
                <CreditCard className="text-gray-400" size={24} />
                <div>
                  <p className="text-xs text-gray-500">Fiyat</p>
                  <p className="font-semibold text-gray-900">{plan?.price > 0 ? plan.price + ' ₺' : 'Ücretsiz'} <span className="text-xs font-normal">/{plan?.billing_cycle === 'yearly' ? 'Yıl' : 'Ay'}</span></p>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg flex items-center gap-3">
                <Calendar className="text-gray-400" size={24} />
                <div>
                  <p className="text-xs text-gray-500">Yenilenme Tarihi</p>
                  <p className="font-semibold text-gray-900">
                    {subscription?.status === 'trial' 
                      ? formatDate(subscription?.trial_ends_at) 
                      : formatDate(subscription?.current_period_end)}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="pt-4 border-t border-gray-100 flex justify-end">
              <Button 
                variant="primary" 
                onClick={() => showError('Yakında online ödeme entegrasyonu hizmete alınacak.')}
                title="Çok Yakında!"
              >
                Paketi Yenile / Yükselt
              </Button>
            </div>
          </div>
        </Card>

        {/* Kullanım İstatistikleri */}
        <Card title="Kullanım Limitleri & İstatistikler">
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-end mb-2">
                <div>
                  <p className="text-sm font-medium text-gray-700">Kullanıcı Limiti</p>
                </div>
                <div className="text-sm">
                  <span className="font-bold text-gray-900">{usersCount}</span> 
                  <span className="text-gray-500"> / {maxUsers > 0 ? maxUsers : '∞'} Personel</span>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className={`h-2.5 rounded-full ${usagePercentage > 90 ? 'bg-red-600' : 'bg-primary-600'}`} style={{ width: `${Math.min(usagePercentage, 100)}%` }}></div>
              </div>
              {maxUsers > 0 && usagePercentage >= 100 && (
                <p className="text-xs text-red-500 mt-2">Maksimum kullanıcı limitine ulaştınız. Yeni kullanıcı eklemek için planınızı yükseltin.</p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 border-t border-gray-100 pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{usage?.total_portfolios || 0}</div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-1">Portföy</div>
              </div>
              <div className="text-center border-l border-gray-100">
                <div className="text-2xl font-bold text-green-600">{usage?.total_customers || 0}</div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-1">Müşteri</div>
              </div>
              <div className="text-center border-l border-gray-100">
                <div className="text-2xl font-bold text-purple-600">{usage?.total_projects || 0}</div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-1">Proje</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionInfo;
